import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Container, Button, Row, Col, Card, Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

const CARD_ORDER_STORAGE_KEY = 'ec2-manager-card-order';
const ARTISTS_ADMIN_EMAILS = ['eenglander@rakefx.com', 'bmcgiveron@rakefx.com'];

const loadStoredCardOrder = () => {
  try {
    const rawValue = window.localStorage.getItem(CARD_ORDER_STORAGE_KEY);
    if (!rawValue) {
      return [];
    }

    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(value => typeof value === 'string' && value.length > 0);
  } catch {
    return [];
  }
};

function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [instances, setInstances] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userName, setUserName] = useState('');
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [statusMessages, setStatusMessages] = useState({});
  const [instanceUserSessions, setInstanceUserSessions] = useState({});
  const [copyMessages, setCopyMessages] = useState({});
  const [copySuccessStates, setCopySuccessStates] = useState({});
  const [rebootLocks, setRebootLocks] = useState({});
  const [cardOrder, setCardOrder] = useState(loadStoredCardOrder);
  const [draggedInstanceId, setDraggedInstanceId] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('');  // State to hold the randomly selected loading message
  const [loadingPercentage, setLoadingPercentage] = useState(0); // State to track the loading percentage
  const backendBaseUrl = `${window.location.origin}/api`;

  // List of random messages
  const loadingMessages = [
    "Spinning up the...spinner.",
    "Poking the cyber-gremlins...",
    "Turning it off and on again...",
    "Placing hamsters on wheels...",
    "Refilling the glitter pouches...",
    "Getting some more coffee...",
    "Jumping the shark...",
    "Crossing the t's and dotting the lowercase j's...",
    "Counting the pixels...",
    "Shaking the magic 8-ball...",
    "Dusting off the cobwebs...",
    "Loosening the screws...",
    "Tightening the bolts...",
    "Cutting the red wire...",
    "Cutting the blue wire...",
    "Cutting the green wire...WAIT, which one is the green wire again?",
    "Serving the server...",
    "Banishing the bugs...",
    "Connecting the tubes...There's a whole series of them you know.",
    "Letting you in just this once...",
    "Wrangling the unicorns...",
    "Reseting teh spelchekr...",
    "Staring won't make this load any faster...",
    "Sorting the bits...",
    "You need a box? I'll get you a box by 3pm tomorrow. Just kidding, I'm a computer, I don't have a car...or a license...or a box.",
    "Calming the chimps...",
    "Looking for my keys...oh wait, I don't have any keys.",
    "Asking the electrons to be more positve...",
    "Sorry about the mess..."
  ];

  useEffect(() => {
    // Select a random loading message when the app starts loading
    setLoadingMessage(loadingMessages[Math.floor(Math.random() * loadingMessages.length)]);

    // Simulate loading percentage
    const interval = setInterval(() => {
      setLoadingPercentage(prev => {
        if (prev < 100) {
          return Math.min(100, prev + Math.floor(Math.random() * 2) + 1); // Increment by either 1 or 2
        } else {
          clearInterval(interval);
          return prev;
        }
      });
    }, 95); // Update percentage every 95ms

    return () => clearInterval(interval);
  }, []);

  const handleLogin = () => {
    window.location.href = `${backendBaseUrl}/login`;
  };

  const handleLogout = () => {
    window.location.href = `${backendBaseUrl}/logout`;
  };

  const isStartAttemptActive = (attempt) => {
    if (!attempt) return false;
    return attempt.running && (attempt.status === 'pending' || attempt.status === 'retrying');
  };

  const formatUptime = (uptimeSeconds) => {
    if (uptimeSeconds === null || uptimeSeconds === undefined || Number.isNaN(Number(uptimeSeconds))) {
      return 'N/A';
    }

    const totalSeconds = Math.max(0, Math.floor(Number(uptimeSeconds)));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0 || days > 0) parts.push(`${hours}h`);
    parts.push(`${minutes}m`);

    return parts.join(' ');
  };

  const formatMemoryMiB = (memoryMiB) => {
    if (memoryMiB === null || memoryMiB === undefined || Number.isNaN(Number(memoryMiB))) {
      return 'N/A';
    }

    const totalMiB = Math.max(0, Number(memoryMiB));
    if (totalMiB === 0) {
      return 'N/A';
    }

    const totalGiB = totalMiB / 1024;
    if (totalGiB >= 1) {
      const roundedGiB = Number.isInteger(totalGiB) ? totalGiB.toFixed(0) : totalGiB.toFixed(1);
      return `${roundedGiB} GB`;
    }

    return `${Math.round(totalMiB)} MB`;
  };

  const formatSessionValue = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') {
      return 'N/A';
    }

    return String(value);
  };

  const formatSessionTimestamp = (value) => {
    if (!value) {
      return '';
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return '';
    }

    return parsedDate.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleToggleInstanceUsers = (instanceId) => {
    const currentPanel = instanceUserSessions[instanceId];
    if (currentPanel?.open && !currentPanel.loading) {
      setInstanceUserSessions(prevSessions => ({
        ...prevSessions,
        [instanceId]: {
          ...prevSessions[instanceId],
          open: false,
        },
      }));
      return;
    }

    setInstanceUserSessions(prevSessions => ({
      ...prevSessions,
      [instanceId]: {
        ...prevSessions[instanceId],
        open: true,
        loading: true,
        error: '',
        message: '',
      },
    }));

    axios.get(`${backendBaseUrl}/instance-users/${encodeURIComponent(instanceId)}`)
      .then(response => {
        const payload = response.data || {};
        setInstanceUserSessions(prevSessions => ({
          ...prevSessions,
          [instanceId]: {
            open: true,
            loading: false,
            error: payload.error || '',
            message: payload.message || '',
            platform: payload.platform || null,
            documentName: payload.documentName || '',
            generatedAt: payload.generatedAt || '',
            sessions: Array.isArray(payload.sessions) ? payload.sessions : [],
          },
        }));
      })
      .catch(error => {
        const message = error.response?.data?.error || 'Unable to fetch users for this workstation.';
        setInstanceUserSessions(prevSessions => ({
          ...prevSessions,
          [instanceId]: {
            open: true,
            loading: false,
            error: message,
            message: '',
            platform: null,
            documentName: '',
            generatedAt: '',
            sessions: [],
          },
        }));
      });
  };

  const handleStopStartAttempt = (instanceId) => {
    axios.post(`${backendBaseUrl}/start-attempt-cancel`, { instanceId })
      .then(response => {
        const message = response.data?.attempt?.message || 'Start attempt cancel requested...';
        setStatusMessages(prevMessages => ({ ...prevMessages, [instanceId]: message }));
        setTimeout(() => {
          setStatusMessages(prevMessages => ({ ...prevMessages, [instanceId]: '' }));
        }, 5000);
      })
      .catch(() => {
        const message = 'Unable to cancel start attempt...';
        setStatusMessages(prevMessages => ({ ...prevMessages, [instanceId]: message }));
        setTimeout(() => {
          setStatusMessages(prevMessages => ({ ...prevMessages, [instanceId]: '' }));
        }, 5000);
      });
  };

  const handleCopyInstanceDetails = async (
    instanceName,
    instanceId,
    instanceIp,
    instanceUptimeSeconds,
    specDetails,
    stackTagValue,
    parsecMachineName,
  ) => {
    const safeSpecDetails = specDetails || {};
    const safeStackTag = String(stackTagValue || '').trim();
    const safeParsecMachineName = String(parsecMachineName || '').trim();
    const copiedAt = new Date();
    const copiedAtDisplay = copiedAt.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    });
    const clipboardLines = [
      `Name: ${instanceName}`,
      `Instance ID: ${instanceId}`,
      `IP: ${instanceIp || 'N/A'}`,
      `Uptime: ${formatUptime(instanceUptimeSeconds)}`,
      `i-type: ${safeSpecDetails.instanceApiName || 'N/A'}`,
      `OS type: ${safeSpecDetails.osType || 'N/A'}`,
      `sys-RAM: ${safeSpecDetails.sysRam || 'N/A'}`,
      `Cores: ${safeSpecDetails.cores || 'N/A'}`,
      `GPU model: ${safeSpecDetails.gpuModel || 'N/A'}`,
      `gpu-RAM: ${safeSpecDetails.gpuRam || 'N/A'}`,
      `Copied by: ${userName || 'Unknown User'}`,
      `Copied at: ${copiedAtDisplay}`,
    ];
    if (safeStackTag) {
      clipboardLines.push(`stack: ${safeStackTag}`);
    }
    if (safeParsecMachineName) {
      clipboardLines.push(`Parsec-MachineName: ${safeParsecMachineName}`);
    }
    const clipboardText = clipboardLines.join('\n');

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(clipboardText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = clipboardText;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setCopyMessages(prevMessages => ({ ...prevMessages, [instanceId]: 'Details copied!' }));
      setCopySuccessStates(prevStates => ({ ...prevStates, [instanceId]: true }));
      setTimeout(() => {
        setCopyMessages(prevMessages => ({ ...prevMessages, [instanceId]: '' }));
      }, 2500);
      setTimeout(() => {
        setCopySuccessStates(prevStates => ({ ...prevStates, [instanceId]: false }));
        if (document.activeElement && typeof document.activeElement.blur === 'function') {
          document.activeElement.blur();
        }
      }, 1200);
    } catch (error) {
      setCopyMessages(prevMessages => ({ ...prevMessages, [instanceId]: 'Copy failed' }));
      setTimeout(() => {
        setCopyMessages(prevMessages => ({ ...prevMessages, [instanceId]: '' }));
      }, 3000);
    }
  };

  const handleInstanceAction = (instanceId, action, state, status) => {
    let message = '';

    if (action === 'reboot' && rebootLocks[instanceId]) {
      message = 'Reboot already in progress...';
      setStatusMessages(prevMessages => ({ ...prevMessages, [instanceId]: message }));
      setTimeout(() => {
        setStatusMessages(prevMessages => ({ ...prevMessages, [instanceId]: '' }));
      }, 5000);
      return;
    }

    // Determine if action is allowed based on the current state and status
    if (action === 'start') {
      if (state === 'running') {
        message = "Can't start workstation when it is already running...";
      } else if (state === 'rebooting') {
        message = "Can't start workstation when it is rebooting...";
      } else if (state === 'shutting-down') {
        message = "Can't start workstation when it is shutting down...";
      } else if (state === 'stopping') {
        message = "Can't start workstation when it is stopping...";
      }
    } else if (action === 'stop' || action === 'reboot') {
      if (!(state === 'running' && status === 'good to go')) {
        message = `Can only ${action} workstation if it is running and good to go...`;
      }
    }

    if (message) {
      // Display the error message
      setStatusMessages(prevMessages => ({ ...prevMessages, [instanceId]: message }));
      setTimeout(() => {
        setStatusMessages(prevMessages => ({ ...prevMessages, [instanceId]: '' }));
      }, 5000);
      return;
    }

    // Proceed with action if valid
    axios.post(`${backendBaseUrl}/instance-action`, { instanceId, action })
      .then(response => {
        if (action === 'start') {
          message = 'Start retry loop initiated...';
        } else if (action === 'reboot') {
          message = 'Workstation received Reboot request...';
          setRebootLocks(prevLocks => ({
            ...prevLocks,
            [instanceId]: {
              sawRebooting: false,
              sawTransition: false,
              requestedAt: Date.now(),
            }
          }));
        } else {
          message = `Workstation received ${action.charAt(0).toUpperCase() + action.slice(1)} request...`;
        }
        setStatusMessages(prevMessages => ({ ...prevMessages, [instanceId]: message }));
        setTimeout(() => {
          setStatusMessages(prevMessages => ({ ...prevMessages, [instanceId]: '' }));
        }, action === 'start' ? 3000 : 5000);
      })
      .catch(error => {
        message = `Error performing ${action} on workstation...`;
        setStatusMessages(prevMessages => ({ ...prevMessages, [instanceId]: message }));
        setTimeout(() => {
          setStatusMessages(prevMessages => ({ ...prevMessages, [instanceId]: '' }));
        }, 5000);
      });
  };

  const handleCardDragStart = (instanceId) => {
    setDraggedInstanceId(instanceId);
  };

  const handleCardDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleCardDrop = (targetInstanceId) => {
    if (!draggedInstanceId || draggedInstanceId === targetInstanceId) {
      setDraggedInstanceId(null);
      return;
    }

    setCardOrder(prevOrder => {
      const normalizedOrder = prevOrder.length
        ? [...prevOrder]
        : (instances || []).map(instance => instance.InstanceId);

      const fromIndex = normalizedOrder.indexOf(draggedInstanceId);
      const toIndex = normalizedOrder.indexOf(targetInstanceId);

      if (fromIndex < 0 || toIndex < 0) {
        return normalizedOrder;
      }

      const updatedOrder = [...normalizedOrder];
      const [moved] = updatedOrder.splice(fromIndex, 1);
      const insertIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
      updatedOrder.splice(Math.max(0, insertIndex), 0, moved);
      return updatedOrder;
    });

    setDraggedInstanceId(null);
  };

  const handleCardDragEnd = () => {
    setDraggedInstanceId(null);
  };

  const fetchInstances = () => {
    if (loggedIn) {
      axios.get(`${backendBaseUrl}/instances`)
        .then(response => {
          const nextInstances = (response.data || []).filter(instance => instance.State !== 'terminated');
          setCardOrder(prevOrder => {
            const fetchedIds = nextInstances.map(instance => instance.InstanceId);
            const kept = prevOrder.filter(instanceId => fetchedIds.includes(instanceId));
            const newIds = fetchedIds.filter(instanceId => !kept.includes(instanceId));
            return [...kept, ...newIds];
          });

          setRebootLocks(prevLocks => {
            const nextLocks = { ...prevLocks };
            const LOCK_TIMEOUT_MS = 10 * 60 * 1000;
            const MIN_REBOOT_LOCK_MS = 15 * 1000;
            const READY_RELEASE_MS = 90 * 1000;

            for (const instance of nextInstances) {
              const lock = nextLocks[instance.InstanceId];
              if (!lock) continue;

              const state = instance.State;
              const statusCheck = instance.StatusCheck;
              const isRebootingNow = state === 'rebooting' || statusCheck === 'rebooting';
              const isReady = state === 'running' && statusCheck === 'good to go';
              const ageMs = Date.now() - lock.requestedAt;

              if (isRebootingNow) {
                nextLocks[instance.InstanceId] = {
                  ...lock,
                  sawRebooting: true,
                  sawTransition: true,
                };
                continue;
              }

              if (!isReady) {
                nextLocks[instance.InstanceId] = {
                  ...lock,
                  sawTransition: true,
                };
                continue;
              }

              if (lock.sawTransition && ageMs >= MIN_REBOOT_LOCK_MS) {
                delete nextLocks[instance.InstanceId];
                continue;
              }

              if (ageMs >= READY_RELEASE_MS) {
                delete nextLocks[instance.InstanceId];
                continue;
              }

              if (ageMs > LOCK_TIMEOUT_MS) {
                delete nextLocks[instance.InstanceId];
              }
            }

            return nextLocks;
          });

          setInstances(nextInstances);
          setLoading(false);
        })
        .catch(error => {
          setError("Oop...Can't fetch instances. Give it a few minutes and reload the page. Please shout if there is no joy.");
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    // Check if the user is logged in
    axios.get(`${backendBaseUrl}/user`)
      .then(response => {
        if (response.data.loggedIn) {
          const userEmail = String(response.data.user?.email || '').trim().toLowerCase();
          setLoggedIn(true);
          setUserName(response.data.user.name);  // Set the user's name
          setIsAdminUser(Boolean(response.data.isAdmin) || ARTISTS_ADMIN_EMAILS.includes(userEmail));
          fetchInstances();  // Fetch instances after confirming login
        } else {
          setLoggedIn(false);
          setIsAdminUser(false);
          setLoading(false);
        }
      })
      .catch(() => {
        setLoggedIn(false);
        setIsAdminUser(false);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(CARD_ORDER_STORAGE_KEY, JSON.stringify(cardOrder));
    } catch {
      // Ignore storage write failures (private mode / storage disabled).
    }
  }, [cardOrder]);

  // Refresh instances every 5 seconds
  useEffect(() => {
    const intervalId = setInterval(fetchInstances, 5000);
    return () => clearInterval(intervalId);  // Cleanup the interval on component unmount
  }, [loggedIn]);

  if (loading) {
    return (
      <div className="loading-shell d-flex flex-column justify-content-center align-items-center">
        <p className="loading-message mb-3">{loadingMessage}</p>
        <Spinner animation="border" role="status" variant="light">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="loading-message mt-3">{Math.min(100, loadingPercentage)}%</p>
      </div>
    );
  }

  const getCardBorderColor = (state, status) => {
    if (state === 'terminated') return 'secondary';
    if (state === 'stopped') return 'secondary';
    if (state === 'running' && status === 'good to go') return 'success';
    return 'warning';
  };

  const orderIndexById = new Map(cardOrder.map((instanceId, index) => [instanceId, index]));
  const orderedInstances = instances
    ? [...instances].sort((a, b) => {
      const aIndex = orderIndexById.has(a.InstanceId) ? orderIndexById.get(a.InstanceId) : Number.MAX_SAFE_INTEGER;
      const bIndex = orderIndexById.has(b.InstanceId) ? orderIndexById.get(b.InstanceId) : Number.MAX_SAFE_INTEGER;
      return aIndex - bIndex;
    })
    : [];

  return (
    <div className="app-shell" style={{ minHeight: '100vh' }}>
      <Container className={loggedIn ? 'py-4' : ''}>
        {error && <div className="error-banner">{error}</div>}

        {loggedIn && instances ? (
          <>
            <section className="panel-header">
              <div className="panel-header-row">
                <h1>{userName}'s Workstations</h1>
                <Button className="topbar-btn" onClick={handleLogout}>Logout</Button>
              </div>
            </section>
            <Row xs={1} md={2} className="g-4 align-items-start" style={{ maxWidth: '900px', margin: '0 auto' }}>
              {orderedInstances.map((instance, idx) => {
                const nameTag = instance.Tags.find(tag => tag.Key === 'Name')?.Value || 'Unnamed Instance';
                const parsecMachineNameTag = (instance.Tags.find(tag => tag.Key === 'Parsec-MachineName')?.Value || '').trim();
                const stackTag = (instance.Tags.find(tag => String(tag.Key || '').toLowerCase() === 'stack')?.Value || instance.StackTag || '').trim();
                const stackDisplay = (instance.StackDisplay || stackTag || 'not tagged').trim();
                const displayName = (instance.DisplayName || (parsecMachineNameTag ? `${nameTag} (${parsecMachineNameTag})` : nameTag)).trim();
                const statusCheck = instance.StatusCheck || 'N/A';  // Assuming status check info is included in instance data
                const rebootLock = rebootLocks[instance.InstanceId] || null;
                const uptimeSeconds = Number(instance.UptimeSeconds || 0);
                const instanceHealth = String(instance.InstanceStatus || '').toLowerCase();
                const systemHealth = String(instance.SystemStatus || '').toLowerCase();
                const ssmConnectionStatus = String(instance.SsmConnectionStatus || '').toLowerCase();
                const hasTransientHealthSignal = (
                  instanceHealth === 'initializing'
                  || instanceHealth === 'insufficient-data'
                  || systemHealth === 'initializing'
                  || systemHealth === 'insufficient-data'
                );
                const hasTransientSsmSignal = (
                  ssmConnectionStatus === 'connectionlost'
                  || ssmConnectionStatus === 'disconnected'
                  || ssmConnectionStatus === 'notconnected'
                  || ssmConnectionStatus === 'not connected'
                );
                const likelyExternalReboot = (
                  !rebootLock
                  && instance.State === 'running'
                  && (statusCheck === 'initializing' || hasTransientHealthSignal || hasTransientSsmSignal)
                  && uptimeSeconds > 300
                );
                const effectiveState = (rebootLock || likelyExternalReboot) ? 'rebooting' : instance.State;
                const effectiveStatusCheck = (rebootLock || likelyExternalReboot) ? 'rebooting' : statusCheck;
                const startAttempt = instance.StartAttempt || null;
                const startAttemptActive = isStartAttemptActive(startAttempt);
                const startAttemptMessage = startAttemptActive ? (startAttempt?.message || '') : '';
                const userSessionsPanel = instanceUserSessions[instance.InstanceId] || null;
                const userSessions = userSessionsPanel?.sessions || [];
                const userSessionsPlatform = String(userSessionsPanel?.platform?.platformType || '').trim();
                const userSessionsCheckedAt = formatSessionTimestamp(userSessionsPanel?.generatedAt);
                const instanceSpecDetails = {
                  instanceApiName: instance.InstanceApiName || instance.InstanceType || 'N/A',
                  osType: instance.OsType || instance.SsmPlatformType || 'N/A',
                  sysRam: formatMemoryMiB(instance.SystemRamMiB),
                  cores: (instance.VCpus !== null && instance.VCpus !== undefined) ? String(instance.VCpus) : 'N/A',
                  gpuModel: instance.GpuModel || 'N/A',
                  gpuRam: formatMemoryMiB(instance.GpuRamMiB),
                };

                return (
                  <Col
                    key={instance.InstanceId}
                    className="align-self-start"
                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}
                    onDragOver={handleCardDragOver}
                    onDrop={() => handleCardDrop(instance.InstanceId)}
                  >
                    <Card
                      border={getCardBorderColor(effectiveState, effectiveStatusCheck)}
                      style={{ width: '30rem', borderWidth: '2.5px' }}
                      draggable
                      onDragStart={() => handleCardDragStart(instance.InstanceId)}
                      onDragEnd={handleCardDragEnd}
                      className={`draggable-instance-card workstation-card ${draggedInstanceId === instance.InstanceId ? 'dragging' : ''}`}
                    >
                      <Card.Header className="card-header-with-copy">
                        <strong className="card-header-title">{displayName}</strong>
                        <span className="copy-card-actions">
                          {copyMessages[instance.InstanceId] && (
                            <span className="copy-card-note">
                              {copyMessages[instance.InstanceId]}
                            </span>
                          )}
                          <button
                            type="button"
                            className={`copy-card-btn ${copySuccessStates[instance.InstanceId] ? 'copy-card-btn-success' : ''}`}
                            onClick={() => handleCopyInstanceDetails(displayName, instance.InstanceId, instance.PrivateIpAddress, instance.UptimeSeconds, instanceSpecDetails, stackTag, parsecMachineNameTag)}
                            aria-label={`Copy details for ${displayName}`}
                            title="Copy instance details"
                          >
                            {!copySuccessStates[instance.InstanceId] ? (
                              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" className="copy-card-icon">
                                <path d="M6.05 4.15H4.8A1.55 1.55 0 0 0 3.25 5.7v5a1.55 1.55 0 0 0 1.55 1.55h5a1.55 1.55 0 0 0 1.55-1.55V9.45" fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" opacity="0.75" />
                                <rect x="6.23" y="1.55" width="7.85" height="7.85" rx="1.55" ry="1.55" fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" className="copy-card-icon copy-card-icon-check">
                                <path d="M6.55 10.2 4.35 8a.75.75 0 1 0-1.06 1.06l2.7 2.7a.75.75 0 0 0 1.06 0l6.2-6.2a.75.75 0 1 0-1.06-1.06L6.55 10.2Z" fill="currentColor" />
                              </svg>
                            )}
                          </button>
                        </span>
                      </Card.Header>
                      <Card.Body>
                        <Card.Title><strong>Instance ID:</strong> {instance.InstanceId}</Card.Title>
                        <Card.Text>
                          <strong>State:</strong> {effectiveState}<br />
                          <strong>Status Check:</strong> {effectiveStatusCheck}<br />
                          <strong>IP:</strong> {instance.PrivateIpAddress || 'N/A'}<br />
                          <strong>Stack:</strong> {stackDisplay}<br />
                          <strong>Uptime:</strong> {formatUptime(instance.UptimeSeconds)}<br />
                        </Card.Text>
                        <hr className="instance-spec-divider" />
                        <div className="instance-spec-table-wrap" role="region" aria-label="Instance hardware details">
                          <table className="instance-spec-table">
                            <tbody>
                              <tr>
                                <th scope="row">i-type</th>
                                <td>{instanceSpecDetails.instanceApiName}</td>
                              </tr>
                              <tr>
                                <th scope="row">OS TYPE</th>
                                <td>{instanceSpecDetails.osType}</td>
                              </tr>
                              <tr>
                                <th scope="row">sys-RAM</th>
                                <td>{instanceSpecDetails.sysRam}</td>
                              </tr>
                              <tr>
                                <th scope="row">Cores</th>
                                <td>{instanceSpecDetails.cores}</td>
                              </tr>
                              <tr>
                                <th scope="row">GPU model</th>
                                <td>{instanceSpecDetails.gpuModel}</td>
                              </tr>
                              <tr>
                                <th scope="row">gpu-RAM</th>
                                <td>{instanceSpecDetails.gpuRam}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <Button
                          variant="outline-light"
                          className="action-btn btn-start-hover"
                          onClick={() => handleInstanceAction(instance.InstanceId, 'start', effectiveState, effectiveStatusCheck)}
                          disabled={effectiveState !== 'stopped' || startAttemptActive}
                        >
                          Start
                        </Button>{' '}
                        {startAttemptActive && (
                          <Button
                            variant="outline-danger"
                            className="action-btn"
                            onClick={() => handleStopStartAttempt(instance.InstanceId)}
                          >
                            Stop Start Attempt
                          </Button>
                        )}{' '}
                        {!startAttemptActive && (
                          <>
                            <Button
                              variant="outline-light"
                              className="action-btn btn-stop-hover"
                              onClick={() => handleInstanceAction(instance.InstanceId, 'stop', effectiveState, effectiveStatusCheck)}
                              disabled={!(effectiveState === 'running' && effectiveStatusCheck === 'good to go')}
                            >
                              Stop
                            </Button>{' '}
                          </>
                        )}
                        <Button
                          variant="outline-light"
                          className="action-btn btn-reboot-hover"
                          onClick={() => handleInstanceAction(instance.InstanceId, 'reboot', effectiveState, effectiveStatusCheck)}
                          disabled={!(effectiveState === 'running' && effectiveStatusCheck === 'good to go') || effectiveStatusCheck === 'rebooting' || Boolean(rebootLock)}
                        >
                          Reboot
                        </Button>{' '}
                        {isAdminUser && (
                          <Button
                            variant="outline-light"
                            className="action-btn btn-users-hover"
                            onClick={() => handleToggleInstanceUsers(instance.InstanceId)}
                          >
                            Users
                          </Button>
                        )}
                        {statusMessages[instance.InstanceId] && (
                          <div className="mt-2 text-info">
                            {statusMessages[instance.InstanceId]}
                          </div>
                        )}
                        {likelyExternalReboot && (
                          <div className="mt-2 text-info">
                            Reboot triggered from instance...
                          </div>
                        )}
                        {startAttemptMessage && (
                          <div className="mt-2 text-secondary">
                            {startAttemptMessage}
                            {startAttempt?.nextRetryInSeconds ? ` (next retry in ${startAttempt.nextRetryInSeconds}s)` : ''}
                          </div>
                        )}
                        {isAdminUser && userSessionsPanel?.open && (
                          <div className="instance-users-panel">
                            <div className="instance-users-header">
                              <strong>Active Sessions</strong>
                              {(userSessionsPlatform || userSessionsCheckedAt) && (
                                <span>
                                  {[userSessionsPlatform, userSessionsCheckedAt ? `Checked ${userSessionsCheckedAt}` : ''].filter(Boolean).join(' | ')}
                                </span>
                              )}
                            </div>
                            {userSessionsPanel.loading ? (
                              <div className="instance-users-empty">
                                <Spinner animation="border" size="sm" role="status" />
                                <span>Checking users...</span>
                              </div>
                            ) : userSessionsPanel.error ? (
                              <div className="instance-users-error">{userSessionsPanel.error}</div>
                            ) : userSessionsPanel.message ? (
                              <div className="instance-users-empty">{userSessionsPanel.message}</div>
                            ) : userSessions.length === 0 ? (
                              <div className="instance-users-empty">No active users found.</div>
                            ) : (
                              <div className="instance-users-table-wrap" role="region" aria-label={`Active sessions for ${displayName}`}>
                                <table className="instance-users-table">
                                  <thead>
                                    <tr>
                                      <th scope="col">User</th>
                                      <th scope="col">Session</th>
                                      <th scope="col">ID</th>
                                      <th scope="col">State</th>
                                      <th scope="col">Idle</th>
                                      <th scope="col">Logon</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {userSessions.map((session, sessionIndex) => (
                                      <tr key={`${instance.InstanceId}-${session.id || sessionIndex}-${session.username || 'session'}`}>
                                        <td>{formatSessionValue(session.username)}</td>
                                        <td>{formatSessionValue(session.sessionName)}</td>
                                        <td>{formatSessionValue(session.id)}</td>
                                        <td>{formatSessionValue(session.state)}</td>
                                        <td>{formatSessionValue(session.idleTime)}</td>
                                        <td>{formatSessionValue(session.logonTime)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </>
        ) : (
          !loggedIn && (
            <div className="login-shell shared-login-shell d-flex flex-column justify-content-center align-items-center">
              <section className="login-panel shared-login-panel text-center">
                <h1 className="shared-login-title">Hi There.</h1>
                <Button className="shared-login-btn" onClick={handleLogin}>Login</Button>
              </section>
            </div>
          )
        )}
      </Container>
    </div>
  );
}

export default App;
